import { Input, InputNumber, Typography } from "antd";
import HCheckbox from "../../../components/custom/input/checkbox/Checkbox";

import React, { useEffect } from "react";
import HRadioGroup from "../../../components/custom/input/radio/HRadioGroup";
import HSelect from "../../../components/custom/input/select/Select";
import type { ActionNodeData } from "../nodes/ActionNode";
import type { FormFieldDefinition, FormFieldVisibleWhen } from "../types/workflowFormField";

const { Text } = Typography;

function readValue(data: ActionNodeData, field: FormFieldDefinition): unknown {
    const raw = data[field.key as keyof ActionNodeData];
    if (raw !== undefined && raw !== null) return raw;
    return field.defaultValue;
}

function readControlValue(data: ActionNodeData, controlKey: string, allFields: FormFieldDefinition[]): unknown {
    const raw = data[controlKey as keyof ActionNodeData];
    if (raw !== undefined && raw !== null) return raw;
    const def = allFields.find((f) => f.key === controlKey);
    return def?.defaultValue;
}

function evaluateVisibleWhen(
    data: ActionNodeData,
    when: FormFieldVisibleWhen | undefined,
    allFields: FormFieldDefinition[]
): boolean {
    if (!when) return true;
    const actual = readControlValue(data, when.field, allFields);
    const op = when.operator ?? "eq";
    const expected = when.value;

    switch (op) {
        case "eq":
            return String(actual) === String(expected);
        case "neq":
            return String(actual) !== String(expected);
        case "in": {
            if (!Array.isArray(expected)) return false;
            return expected.map(String).includes(String(actual));
        }
        case "notIn": {
            if (!Array.isArray(expected)) return false;
            return !expected.map(String).includes(String(actual));
        }
        case "gt": {
            const a = Number(actual);
            const b = typeof expected === "number" ? expected : Number(expected);
            return Number.isFinite(a) && Number.isFinite(b) && a > b;
        }
        case "lt": {
            const a = Number(actual);
            const b = typeof expected === "number" ? expected : Number(expected);
            return Number.isFinite(a) && Number.isFinite(b) && a < b;
        }
        default:
            return true;
    }
}

const DynamicCheckboxField: React.FC<{
    field: FormFieldDefinition;
    data: ActionNodeData;
    update: (partial: Partial<ActionNodeData>) => void;
}> = ({ field, data, update }) => {
    const readOnlyPinnedTrue = Boolean(field.readOnly && field.defaultValue === true);
    const rawStored = data[field.key as keyof ActionNodeData];

    useEffect(() => {
        if (!readOnlyPinnedTrue) return;
        const isTrue =
            rawStored === true ||
            rawStored === "true" ||
            (typeof rawStored === "string" && rawStored.toLowerCase() === "true");
        if (!isTrue) {
            update({ [field.key]: true } as Partial<ActionNodeData>);
        }
    }, [readOnlyPinnedTrue, field.key, rawStored, update]);

    const value = readValue(data, field);
    const checked = readOnlyPinnedTrue
        ? true
        : typeof value === "boolean"
          ? value
          : value === "true" || String(value).toLowerCase() === "true";

    return (
        <div className="d-flex flex-column gap-2 mb-3 hs-fw-300-i hs-color-dark-grey-2">
            <HCheckbox
                variant="checkbox"
                color="BlackRussian"
                checked={checked}
                disabled={field.readOnly}
                item={{ label: field.label, value: checked }}
                onChange={(e: { target: { checked: boolean } }) => {
                    if (field.readOnly) return;
                    update({ [field.key]: e.target.checked } as Partial<ActionNodeData>);
                }}
            />
        </div>
    );
};

const DynamicFormField: React.FC<{
    field: FormFieldDefinition;
    data: ActionNodeData;
    update: (partial: Partial<ActionNodeData>) => void;
}> = ({ field, data, update }) => {
    const value = readValue(data, field);

    switch (field.type) {
        case "radio": {
            const opts = field.options ?? [];
            const fallback =
                typeof field.defaultValue === "string" ? field.defaultValue : (opts[0]?.value ?? "");
            const current = typeof value === "string" ? value : fallback;
            return (
                <div className="mb-3">
                    <HRadioGroup
                        groupLabel={field.label}
                        options={opts.map((o) => ({ label: o.label, value: o.value }))}
                        radioGroupProps={{
                            value: current,
                            onChange: (e: { target: { value: string } }) =>
                                update({ [field.key]: e.target.value } as Partial<ActionNodeData>),
                            className: "d-flex flex-column gap-2",
                        }}
                    />
                </div>
            );
        }
        case "checkbox": {
            return <DynamicCheckboxField field={field} data={data} update={update} />;
        }
        case "select": {
            const opts = field.options ?? [];
            const current = typeof value === "string" ? value : String(value ?? field.defaultValue ?? "");
            return (
                <div className="mb-3">
                    <Text className="hs-fs-12 hs-color-BlackRussian hs-fw-500 d-block mb-2">{field.label}</Text>
                    <HSelect
                        value={current || undefined}
                        onChange={(v: string) => update({ [field.key]: v } as Partial<ActionNodeData>)}
                        placeholder={field.placeholder ?? "Select"}
                        disabled={field.readOnly}
                        options={opts.map((o) => ({ label: o.label, value: o.value }))}
                    />
                </div>
            );
        }
        case "text": {
            const current = typeof value === "string" ? value : value != null ? String(value) : "";
            return (
                <div className="mb-3">
                    <Text className="hs-fs-12 hs-color-BlackRussian hs-fw-500 d-block mb-2">{field.label}</Text>
                    <Input
                        className="w-100"
                        value={current}
                        placeholder={field.placeholder}
                        readOnly={field.readOnly}
                        onChange={(e) => update({ [field.key]: e.target.value } as Partial<ActionNodeData>)}
                    />
                </div>
            );
        }
        case "textarea": {
            const current = typeof value === "string" ? value : value != null ? String(value) : "";
            return (
                <div className="mb-3">
                    <Text className="hs-fs-12 hs-color-BlackRussian hs-fw-500 d-block mb-2">{field.label}</Text>
                    <Input.TextArea
                        className="w-100"
                        value={current}
                        placeholder={field.placeholder}
                        readOnly={field.readOnly}
                        rows={4}
                        onChange={(e) => update({ [field.key]: e.target.value } as Partial<ActionNodeData>)}
                    />
                </div>
            );
        }
        case "number": {
            const num =
                typeof value === "number"
                    ? value
                    : typeof value === "string" && value !== ""
                      ? Number(value)
                      : typeof field.defaultValue === "number"
                        ? field.defaultValue
                        : undefined;
            return (
                <div className="mb-3">
                    <Text className="hs-fs-12 hs-color-BlackRussian hs-fw-500 d-block mb-2">{field.label}</Text>
                    <InputNumber
                        className="w-100"
                        value={num}
                        min={field.min}
                        max={field.max}
                        readOnly={field.readOnly}
                        placeholder={field.placeholder}
                        onChange={(v) => update({ [field.key]: v ?? 0 } as Partial<ActionNodeData>)}
                    />
                </div>
            );
        }
        default:
            return null;
    }
};

export const DynamicStepForm: React.FC<{
    fields: FormFieldDefinition[];
    data: ActionNodeData;
    update: (partial: Partial<ActionNodeData>) => void;
}> = ({ fields, data, update }) => (
    <div>
        {fields.map((field) =>
            evaluateVisibleWhen(data, field.visibleWhen, fields) ? (
                <DynamicFormField key={field.key} field={field} data={data} update={update} />
            ) : null
        )}
    </div>
);
